<template>
  <section class="content">
    <div class="container-fluid">
      <div class="card">
        <div class="card-header d-flex justify-content-between align-center">
          <h3 class="card-title">LOGS ACTIVITY USER</h3>
        </div>

        <div class="card-body">
          <!-- FILTER BAR -->
          <div class="row mb-3">
            <div class="col-md-3">
              <v-select
                v-model="filter.log_name"
                label="Filter Log Group"
                dense
                clearable
                outlined
                hide-details
                :items="logNameItems"
                @change="onFilterChange"
              />
            </div>

            <div class="col-md-3">
              <v-select
                v-model="filter.causer_id"
                label="Filter User / Pelaku"
                dense
                clearable
                outlined
                hide-details
                :items="userItems"
                item-text="name"
                item-value="id"
                @change="onFilterChange"
              />
            </div>

            <div class="col-md-3">
              <v-menu
                v-model="menuTanggal"
                :close-on-content-click="false"
                offset-y
                max-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="tanggalLabel"
                    label="Range Tanggal"
                    dense
                    outlined
                    readonly
                    clearable
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="clearTanggal"
                  />
                </template>

                <v-date-picker
                  v-model="filter.tanggal"
                  range
                  locale="id-ID"
                  @change="onTanggalChange"
                />
              </v-menu>
            </div>

            <div class="col-md-3">
              <v-text-field
                v-model="filter.search"
                label="Cari Deskripsi / Subject / User"
                dense
                outlined
                clearable
                hide-details
                append-icon="mdi-magnify"
                @input="onFilterChange"
              />
            </div>
          </div>

          <!-- DATA TABLE -->
          <v-data-table
            :headers="headers"
            :items="items"
            :loading="loading"
            :items-per-page.sync="pagination.itemsPerPage"
            :page.sync="pagination.page"
            :server-items-length="totalItems"
            :footer-props="{
              'items-per-page-options': [5, 10, 25, 50, 100]
            }"
            @update:page="fetchData"
            @update:items-per-page="changeItemsPerPage"
            class="elevation-1"
          >
            <template #item.no="{ index }">
              {{ (pagination.page - 1) * pagination.itemsPerPage + index + 1 }}
            </template>

            <template #item.created_at="{ item }">
              {{ formatTanggal(item.created_at) }}
            </template>

            <template #item.log_name="{ item }">
              <v-chip small dark :color="getLogColor(item.log_name)">
                {{ item.log_name ? item.log_name.toUpperCase() : 'DEFAULT' }}
              </v-chip>
            </template>

            <template #item.causer="{ item }">
              <div v-if="item.causer_name">
                <strong>{{ item.causer_name }}</strong>
                <div class="caption text-muted">{{ item.causer_email }}</div>
              </div>
              <div v-else class="text-muted font-italic">
                System / Unknown
              </div>
            </template>

            <template #item.ip_address="{ item }">
              <span>{{ getIpAddress(item) }}</span>
            </template>

            <template #item.action="{ item }">
              <v-btn small icon color="primary" @click="showDetail(item)" title="Lihat Detail Log">
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </div>
      </div>
    </div>

    <!-- DIALOG DETAIL -->
    <v-dialog v-model="dialogDetail" max-width="750">
      <v-card v-if="selectedItem">
        <v-card-title class="headline primary white--text">
          Detail Activity Log #{{ selectedItem.id }}
        </v-card-title>

        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="12" sm="6">
              <strong>Waktu:</strong> {{ formatTanggal(selectedItem.created_at) }}
            </v-col>
            <v-col cols="12" sm="6">
              <strong>Log Name:</strong>
              <v-chip small class="ml-2" dark :color="getLogColor(selectedItem.log_name)">
                {{ selectedItem.log_name }}
              </v-chip>
            </v-col>
            <v-col cols="12" sm="6">
              <strong>Pelaku / User:</strong>
              {{ selectedItem.causer_name || 'System' }}
              <span v-if="selectedItem.causer_email">({{ selectedItem.causer_email }})</span>
            </v-col>
            <v-col cols="12" sm="6">
              <strong>IP Address:</strong> {{ getIpAddress(selectedItem) }}
            </v-col>
            <v-col cols="12" sm="6" v-if="getSubjectInfo(selectedItem)">
              <strong>Subject:</strong> {{ getSubjectInfo(selectedItem) }}
            </v-col>
            <v-col cols="12" sm="6" v-if="getTable(selectedItem)">
              <strong>Tabel DB:</strong> {{ getTable(selectedItem) }}
            </v-col>
            <v-col cols="12">
              <strong>Deskripsi Aksi:</strong>
              <div class="pa-2 bg-light rounded mt-1 border">
                {{ selectedItem.description }}
              </div>
            </v-col>
          </v-row>

          <!-- TABEL PERUBAHAN DATA (JIKA ADA OLD & ATTRIBUTES) -->
          <div v-if="hasChanges(selectedItem)" class="mt-4">
            <h4 class="mb-2 text-primary">
              <v-icon color="primary" small>mdi-swap-horizontal</v-icon>
              Perubahan Field (Old vs New)
            </h4>
            <v-simple-table dense class="border">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Field</th>
                    <th class="text-left">Nilai Lama (Before)</th>
                    <th class="text-left">Nilai Baru (After)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(val, key) in getChangedAttributes(selectedItem)" :key="key">
                    <td><code>{{ key }}</code></td>
                    <td class="red--text text--darken-2">
                      {{ formatVal(getOldVal(selectedItem, key)) }}
                    </td>
                    <td class="green--text text--darken-2">
                      {{ formatVal(val) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>

          <!-- USER AGENT -->
          <div v-if="getUserAgent(selectedItem)" class="mt-3">
            <div class="caption text-muted">
              <strong>User Agent:</strong> {{ getUserAgent(selectedItem) }}
            </div>
          </div>

          <!-- RAW PROPERTIES JSON -->
          <div class="mt-3">
            <v-expansion-panels flat compact>
              <v-expansion-panel>
                <v-expansion-panel-header class="pa-0 font-weight-bold grey--text text--darken-2">
                  Lihat Raw JSON Properties
                </v-expansion-panel-header>
                <v-expansion-panel-content class="pa-0">
                  <pre class="bg-dark text-light p-3 rounded" style="white-space: pre-wrap; word-break: break-all; max-height: 250px; overflow-y: auto;">{{ JSON.stringify(selectedItem.properties, null, 2) }}</pre>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dialogDetail = false">Tutup</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
const getTodayFormatted = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export default {
  name: 'ActivityLogList',

  data() {
    const today = getTodayFormatted()

    return {
      loading: false,
      items: [],
      totalItems: 0,
      logNameItems: [],
      userItems: [],
      dialogDetail: false,
      selectedItem: null,
      menuTanggal: false,

      pagination: {
        page: 1,
        itemsPerPage: 10
      },

      filter: {
        log_name: null,
        causer_id: null,
        search: null,
        tanggal: [today, today]
      },

      headers: [
        { text: 'No', value: 'no', width: 50, sortable: false },
        { text: 'Waktu', value: 'created_at', width: 160 },
        { text: 'Group Log', value: 'log_name', width: 140 },
        { text: 'User / Pelaku', value: 'causer', width: 180 },
        { text: 'Deskripsi Aksi', value: 'description' },
        { text: 'IP Address', value: 'ip_address', width: 130 },
        { text: 'Detail', value: 'action', width: 70, sortable: false }
      ]
    }
  },

  computed: {
    tanggalLabel() {
      if (!this.filter.tanggal || this.filter.tanggal.length === 0) {
        return ''
      }
      if (
        this.filter.tanggal.length === 1 ||
        this.filter.tanggal[0] === this.filter.tanggal[1]
      ) {
        return this.formatDateShort(this.filter.tanggal[0])
      }
      if (this.filter.tanggal.length === 2) {
        const [start, end] = this.filter.tanggal
        return `${this.formatDateShort(start)} - ${this.formatDateShort(end)}`
      }
      return ''
    }
  },

  mounted() {
    this.fetchData()
  },

  methods: {
    formatDateShort(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    },

    formatTanggal(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },

    getLogColor(logName) {
      if (!logName) return 'grey'
      const colors = {
        pelanggan: 'indigo',
        pelanggan_internet: 'teal',
        user: 'purple',
        auth: 'blue-grey',
        keuangan: 'amber darken-2',
        utang: 'deep-orange darken-2',
        piutang: 'deep-purple darken-1',
        kick: 'red darken-2',
        router: 'deep-orange',
        paket: 'light-blue darken-1',
        kantor: 'cyan darken-2',
        role: 'pink darken-1',
        sales: 'green darken-1'
      }
      return colors[logName] || 'blue'
    },

    getIpAddress(item) {
      if (item && item.properties && item.properties.ip_address) {
        return item.properties.ip_address
      }
      return '-'
    },

    getUserAgent(item) {
      if (item && item.properties && item.properties.user_agent) {
        return item.properties.user_agent
      }
      return ''
    },

    getTable(item) {
      if (item && item.properties && item.properties.table) {
        return item.properties.table
      }
      return ''
    },

    getSubjectInfo(item) {
      if (!item) return ''
      if (item.subject_type || item.subject_id) {
        return `${item.subject_type || ''} #${item.subject_id || ''}`
      }
      return ''
    },

    hasChanges(item) {
      if (!item || !item.properties) return false
      return Boolean(
        item.properties.attributes &&
          Object.keys(item.properties.attributes).length > 0
      )
    },

    getChangedAttributes(item) {
      if (item && item.properties && item.properties.attributes) {
        return item.properties.attributes
      }
      return {}
    },

    getOldVal(item, key) {
      if (
        item &&
        item.properties &&
        item.properties.old &&
        item.properties.old[key] !== undefined
      ) {
        return item.properties.old[key]
      }
      return '-'
    },

    formatVal(val) {
      if (val === null || val === undefined || val === '') return '(Kosong)'
      if (typeof val === 'boolean') return val ? 'true' : 'false'
      if (typeof val === 'object') return JSON.stringify(val)
      return val
    },

    onFilterChange() {
      this.pagination.page = 1
      this.fetchData()
    },

    onTanggalChange(val) {
      if (val.length === 2) {
        this.menuTanggal = false
        this.onFilterChange()
      } else if (val.length === 1) {
        this.onFilterChange()
      }
    },

    clearTanggal() {
      this.filter.tanggal = []
      this.onFilterChange()
    },

    async fetchData() {
      this.loading = true
      try {
        const dateFrom =
          this.filter.tanggal && this.filter.tanggal.length > 0
            ? this.filter.tanggal[0]
            : null
        const dateTo =
          this.filter.tanggal && this.filter.tanggal.length > 1
            ? this.filter.tanggal[1]
            : dateFrom

        const res = await axios.get('/api/logs/activity', {
          params: {
            page: this.pagination.page,
            per_page: this.pagination.itemsPerPage,
            log_name: this.filter.log_name,
            causer_id: this.filter.causer_id,
            search: this.filter.search,
            date_from: dateFrom,
            date_to: dateTo
          }
        })

        this.items = res.data.data.data || []
        this.totalItems = res.data.data.total || 0
        if (res.data.log_names) {
          this.logNameItems = res.data.log_names
        }
        if (res.data.users) {
          this.userItems = res.data.users
        }
      } catch (error) {
        if (typeof Toast !== 'undefined') {
          Toast.fire({
            icon: 'error',
            title:
              error.response?.data?.message || 'Gagal memuat log activity'
          })
        }
      } finally {
        this.loading = false
      }
    },

    changeItemsPerPage(val) {
      if (val && this.pagination.itemsPerPage !== val) {
        this.pagination.itemsPerPage = val
        this.pagination.page = 1
        this.fetchData()
      }
    },

    showDetail(item) {
      this.selectedItem = item
      this.dialogDetail = true
    }
  }
}
</script>

<style scoped>
.border {
  border: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
